import React, { useEffect, useRef, useState } from 'react';
import { portalApi } from '../../../lib/portalApi';
import { statusChip } from './KanbanBoard';

/**
 * Shared batch-task detail drawer for admin + founder.
 * Admin can edit title/description/deadline and reopen tasks.
 * Both roles can manage checklist to-dos and comments.
 */
export default function BatchTaskDetailPanel({
  taskId,
  isAdmin = false,
  assigneeOptions = [],
  onClose,
  onChanged,
}) {
  const [task, setTask] = useState(null);
  const [error, setError] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editTaskDate, setEditTaskDate] = useState('');
  const [editDeadline, setEditDeadline] = useState('');
  const [editing, setEditing] = useState(false);
  const [todoTitle, setTodoTitle] = useState('');
  const [comment, setComment] = useState('');
  const [momDraft, setMomDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const load = async () => {
    const t = await portalApi.getBatchTask(taskId);
    setTask(t);
    setEditTitle(t.title || '');
    setEditDesc(t.description || '');
    setMomDraft(t.meeting?.mom || '');
    if (t.taskDate) {
      const td = new Date(t.taskDate);
      const ymd = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(td);
      setEditTaskDate(ymd);
    } else {
      setEditTaskDate('');
    }
    if (t.deadline || t.dueAt) {
      const d = new Date(t.deadline || t.dueAt);
      const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
      setEditDeadline(local);
    } else {
      setEditDeadline('');
    }
  };

  useEffect(() => {
    if (!taskId) return undefined;
    setError('');
    load().catch((e) => setError(e.message));
    return undefined;
  }, [taskId]);

  const run = async (fn) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError('');
    try {
      await fn();
      await load();
      onChanged?.();
    } catch (e) {
      setError(e.message);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  if (!taskId) return null;

  const isDaily = task?.kind === 'daily' || task?.isDaily;
  const assignedByText =
    task?.assignedByName ||
    task?.assignedBy?.name ||
    (task?.assignedById ? 'Administrator' : 'Not recorded');
  const createdByText =
    task?.createdByName ||
    task?.createdBy?.name ||
    (task?.createdById ? 'Administrator' : 'Not recorded');

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-black/60"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="relative w-full max-w-lg h-full overflow-y-auto bg-[#0c0c10] border-l border-white/10 p-5 sm:p-6 space-y-5 shadow-2xl">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              {task?.code ? (
                <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-white/10 text-white/60">
                  {task.code}
                </span>
              ) : null}
              {task ? statusChip(task.status) : null}
              {isDaily ? (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-[#d42b92]/20 text-[#ff8ec5] border border-[#d42b92]/40">
                  Daily Task
                </span>
              ) : (
                <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-white/5 text-white/40">
                  {(task?.kind || '').replace(/_/g, ' ')}
                </span>
              )}
            </div>
            {!editing ? (
              <>
                <h2 className="text-xl font-semibold text-white leading-snug">{task?.title || '…'}</h2>
                {task?.description ? (
                  <p className="text-sm text-white/55 leading-relaxed">{task.description}</p>
                ) : null}
                <div className="space-y-1 text-xs pt-1 border-t border-white/5 text-white/60">
                  {isDaily && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-white/40">Assigned by:</span>
                      <span className="text-white/90 font-medium">{assignedByText}</span>
                    </div>
                  )}
                  {task?.assigneeName && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-white/40">Assigned to:</span>
                      <span className="text-white/80 font-medium">{task.assigneeName}</span>
                    </div>
                  )}
                  {(task?.createdByName || task?.createdBy?.name) && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-white/40">Created by:</span>
                      <span className="text-white/70">{createdByText}</span>
                    </div>
                  )}
                  {task?.taskDate && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-white/40">Task date:</span>
                      <span className="text-white/80">{new Date(task.taskDate).toLocaleDateString()}</span>
                    </div>
                  )}
                  {(task?.deadline || task?.dueAt) && (
                    <div className="flex items-center gap-1.5 text-[#ff8ec5]">
                      <span className="text-white/40">Deadline:</span>
                      <span>{new Date(task.deadline || task.dueAt).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="space-y-2 w-full">
                <input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
                  placeholder="Title"
                />
                <textarea
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  rows={3}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
                  placeholder="Description"
                />
                {isDaily && (
                  <label className="block text-xs text-white/40">
                    Task date
                    <input
                      type="date"
                      value={editTaskDate}
                      onChange={(e) => setEditTaskDate(e.target.value)}
                      className="mt-1 w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
                    />
                  </label>
                )}
                <label className="block text-xs text-white/40">
                  Deadline
                  <input
                    type="datetime-local"
                    value={editDeadline}
                    onChange={(e) => setEditDeadline(e.target.value)}
                    className="mt-1 w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
                  />
                </label>
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} className="text-white/40 hover:text-white text-lg leading-none">
            ×
          </button>
        </div>

        {error && <p className="text-red-400 text-sm">{error}</p>}

        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            {!editing ? (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-xs px-3 py-1.5 rounded-lg border border-white/15 text-white/80"
              >
                Edit task
              </button>
            ) : (
              <>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    run(async () => {
                      await portalApi.patchBatchTask(task.id, {
                        title: editTitle.trim(),
                        description: editDesc,
                        taskDate: isDaily ? (editTaskDate || undefined) : undefined,
                        deadline: editDeadline
                          ? new Date(editDeadline).toISOString()
                          : null,
                      });
                      setEditing(false);
                    })
                  }
                  className="text-xs px-3 py-1.5 rounded-lg bg-[#d42b92] text-white disabled:opacity-50"
                >
                  {busy ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setEditing(false)}
                  className="text-xs px-3 py-1.5 rounded-lg border border-white/15 text-white/60"
                >
                  Cancel
                </button>
              </>
            )}
            {assigneeOptions.length > 0 && (
              <select
                value={task?.assigneeId || ''}
                disabled={busy}
                onChange={(e) =>
                  run(() =>
                    portalApi.patchBatchTask(task.id, { assigneeId: e.target.value || null })
                  )
                }
                className="text-xs bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-white disabled:opacity-50"
              >
                <option value="">Unassigned</option>
                {assigneeOptions.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.name || m.email}
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              disabled={busy || !task}
              onClick={() => {
                if (!window.confirm(`Delete task “${task?.title || ''}”? This cannot be undone.`)) {
                  return;
                }
                run(async () => {
                  await portalApi.deleteBatchTask(task.id);
                  onClose?.();
                });
              }}
              className="text-xs px-3 py-1.5 rounded-lg border border-red-500/40 text-red-300 disabled:opacity-50"
            >
              {busy ? 'Working…' : 'Delete task'}
            </button>
          </div>
        )}

        {busy && (
          <p className="text-xs text-[#ff8ec5]/80" aria-live="polite">
            Please wait, request in progress…
          </p>
        )}

        {task?.kind === 'meeting' && (
          <div className="border-t border-white/10 pt-4 space-y-2">
            <div className="text-sm text-white font-medium">Minutes of Meeting</div>
            {task.meeting && (
              <p className="text-xs text-white/40">
                {task.meeting.mode || 'online'}
                {task.meeting.scheduledAt
                  ? ` · ${new Date(task.meeting.scheduledAt).toLocaleString()}`
                  : ''}
                {task.meeting.meetingLink ? (
                  <>
                    {' '}
                    ·{' '}
                    <a
                      href={task.meeting.meetingLink}
                      className="text-[#ff8ec5]"
                      target="_blank"
                      rel="noreferrer"
                    >
                      link
                    </a>
                  </>
                ) : null}
              </p>
            )}
            {isAdmin ? (
              <>
                <textarea
                  rows={4}
                  value={momDraft}
                  onChange={(e) => setMomDraft(e.target.value)}
                  placeholder="Add MOM after the meeting…"
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
                />
                {momDraft !== (task.meeting?.mom || '') && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      run(() => portalApi.updateBatchMeeting(task.id, { mom: momDraft }))
                    }
                    className="text-xs px-3 py-1.5 rounded-lg bg-[#d42b92] text-white disabled:opacity-40"
                  >
                    Save MOM
                  </button>
                )}
              </>
            ) : task.meeting?.mom ? (
              <p className="text-sm text-white/70 whitespace-pre-wrap bg-black/30 rounded-xl p-3">
                {task.meeting.mom}
              </p>
            ) : (
              <p className="text-xs text-white/35">No minutes yet.</p>
            )}
          </div>
        )}

        {/* Manager status controls, any status ↔ any status */}
        {isAdmin && task && (
          <div className="border-t border-white/10 pt-4 space-y-2">
            <div className="text-sm text-white font-medium">Set status</div>
            <div className="flex flex-wrap gap-2">
              {[
                ['todo', 'To do'],
                ['in_progress', 'In progress'],
                ['on_hold', 'On hold'],
                ['blocked', 'Blocked'],
                ['clarification', 'Clarification'],
                ['ready_for_review', 'Review'],
                ['rework', 'Rework'],
                ['completed', 'Completed'],
                ['cancelled', 'Cancelled'],
              ].map(([status, label]) => (
                <button
                  key={status}
                  type="button"
                  disabled={busy || task.status === status}
                  onClick={() =>
                    run(() =>
                      portalApi.transitionTask(
                        task.id,
                        status,
                        ['on_hold', 'blocked', 'clarification', 'cancelled'].includes(status)
                          ? `${label} by admin`
                          : undefined
                      )
                    )
                  }
                  className={`text-xs px-3 py-1.5 rounded-lg border ${
                    task.status === status
                      ? 'border-[#d42b92] bg-[#d42b92]/20 text-white'
                      : 'border-white/15 text-white/70 hover:border-[#d42b92]/50'
                  } disabled:opacity-40`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Action items / to-do */}
        <div className="border-t border-white/10 pt-4 space-y-2">
          <div className="text-sm text-white font-medium">Action items</div>
          {(task?.checklist || []).map((a) => (
            <div key={a.id} className="flex items-center gap-2 text-sm text-white/70">
              <input
                type="checkbox"
                checked={!!a.done}
                onChange={() =>
                  run(() => portalApi.patchBatchChecklist(task.id, a.id, { done: !a.done }))
                }
              />
              <span className={a.done ? 'line-through opacity-50' : ''}>{a.title || a.text}</span>
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => run(() => portalApi.deleteBatchChecklist(task.id, a.id))}
                  className="ml-auto text-xs text-white/30 hover:text-red-400"
                >
                  Delete
                </button>
              )}
            </div>
          ))}
          <div className="flex gap-2">
            <input
              value={todoTitle}
              onChange={(e) => setTodoTitle(e.target.value)}
              placeholder="Add new action item…"
              className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-1.5 text-white text-sm"
            />
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                run(async () => {
                  if (!todoTitle.trim()) return;
                  await portalApi.addBatchChecklist(task.id, todoTitle.trim());
                  setTodoTitle('');
                })
              }
              className="px-3 py-1.5 rounded-lg border border-white/15 text-xs text-white"
            >
              Add
            </button>
          </div>
        </div>

        {/* Comments */}
        <div className="border-t border-white/10 pt-4 space-y-2">
          <div className="text-sm text-white font-medium">
            Comments ({task?.comments?.length || 0})
          </div>
          {(task?.comments || []).length === 0 && (
            <p className="text-xs text-white/35">No comments yet. Start the conversation below.</p>
          )}
          {(task?.comments || []).map((c) => (
            <div key={c.id} className="text-sm text-white/60 rounded-xl bg-white/[0.03] px-3 py-2">
              <span className="text-white/40">{c.author || 'User'}: </span>
              {c.body}
              {c.createdAt ? (
                <div className="text-[10px] text-white/25 mt-1">
                  {new Date(c.createdAt).toLocaleString()}
                </div>
              ) : null}
            </div>
          ))}
          <div className="flex gap-2">
            <input
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a comment…"
              className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-1.5 text-white text-sm"
            />
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                run(async () => {
                  if (!comment.trim()) return;
                  await portalApi.addBatchComment(task.id, comment.trim());
                  setComment('');
                })
              }
              className="px-3 py-1.5 rounded-lg bg-[#d42b92] text-white text-xs"
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
